import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
        Box,
        Typography,
        Card,
        CardContent,
        TextField,
        Button,
        Alert,
        CircularProgress,
        InputAdornment,
        Paper,
        FormControl,
        InputLabel,
        Select,
        MenuItem,
        Divider
} from '@mui/material';
import {
        Payment as PaymentIcon,
        ArrowForward as ArrowForwardIcon,
        AccountBalance as AccountBalanceIcon,
        Receipt as ReceiptIcon
} from '@mui/icons-material';
import hmrcApi from '../api/services/hmrc';

type TaxRegime = 'SelfAssessment' | 'Vat';

const HMRCPayment: React.FC = () => {
        const navigate = useNavigate();
        const [taxRegime, setTaxRegime] = useState<TaxRegime>('Vat');
        const [reference, setReference] = useState('');
        const [amount, setAmount] = useState<string>('');
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);

        const getReferenceLabel = () => {
                return taxRegime === 'SelfAssessment'
                        ? 'Unique Taxpayer Reference (UTR)'
                        : 'VAT Registration Number (VRN)';
        };

        const getReferencePlaceholder = () => {
                return taxRegime === 'SelfAssessment'
                        ? '1234567890'
                        : '123456789';
        };

        const getReferenceHelperText = () => {
                return taxRegime === 'SelfAssessment'
                        ? '10-digit UTR from your Self Assessment registration'
                        : '9-digit VRN from your VAT registration';
        };

        const validateReference = (): boolean => {
                if (taxRegime === 'SelfAssessment') {
                        return /^\d{10}$/.test(reference);
                } else {
                        return /^\d{9}$/.test(reference);
                }
        };

        const handlePayment = async () => {
                const parsedAmount = parseFloat(amount);
                if (isNaN(parsedAmount) || parsedAmount < 0) {
                        setError('Please enter a valid amount (£0 or more).');
                        return;
                }

                if (!reference.trim()) {
                        setError(`Please enter your ${getReferenceLabel()}.`);
                        return;
                }

                if (!validateReference()) {
                        setError(`Invalid ${getReferenceLabel()}. ${getReferenceHelperText()}.`);
                        return;
                }

                try {
                        setLoading(true);
                        setError(null);

                        const amountInPence = Math.round(parsedAmount * 100);
                        const currentUrl = window.location.origin;
                        const backUrl = `${currentUrl}/hmrc/pay`;

                        const result = await hmrcApi.initiatePayment(
                                taxRegime,
                                reference,
                                amountInPence,
                                backUrl
                        );

                        if (result.success && result.redirect_url) {
                                // Redirect user to Gov.uk Pay
                                window.location.href = result.redirect_url;
                        } else {
                                setError('Failed to initiate payment. Please try again.');
                        }
                } catch (err: any) {
                        setError(err.message || 'An error occurred while initiating payment.');
                } finally {
                        setLoading(false);
                }
        };

        return (
                <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
                        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PaymentIcon color="primary" />
                                Pay HMRC
                        </Typography>
                        <Typography variant="body1" color="text.secondary" paragraph>
                                Make a tax payment to HMRC via the secure Gov.uk Pay service.
                        </Typography>

                        {error && (
                                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                                        {error}
                                </Alert>
                        )}

                        <Card elevation={3}>
                                <CardContent sx={{ p: 4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                                <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                                                <Box>
                                                        <Typography variant="h6">HMRC Payment</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                                Select tax type and enter payment details
                                                        </Typography>
                                                </Box>
                                        </Box>

                                        <FormControl fullWidth sx={{ mb: 3 }}>
                                                <InputLabel>Tax Type</InputLabel>
                                                <Select
                                                        value={taxRegime}
                                                        label="Tax Type"
                                                        onChange={(e) => {
                                                                setTaxRegime(e.target.value as TaxRegime);
                                                                setReference(''); // Clear reference when changing tax type
                                                        }}
                                                >
                                                        <MenuItem value="Vat">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <ReceiptIcon fontSize="small" />
                                                                        VAT (Value Added Tax)
                                                                </Box>
                                                        </MenuItem>
                                                        <MenuItem value="SelfAssessment">
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <AccountBalanceIcon fontSize="small" />
                                                                        Self Assessment
                                                                </Box>
                                                        </MenuItem>
                                                </Select>
                                        </FormControl>

                                        <TextField
                                                label={getReferenceLabel()}
                                                value={reference}
                                                onChange={(e) => setReference(e.target.value.replace(/\D/g, ''))}
                                                fullWidth
                                                sx={{ mb: 3 }}
                                                placeholder={getReferencePlaceholder()}
                                                helperText={getReferenceHelperText()}
                                                inputProps={{
                                                        maxLength: taxRegime === 'SelfAssessment' ? 10 : 9,
                                                        inputMode: 'numeric'
                                                }}
                                        />

                                        <Divider sx={{ my: 2 }} />

                                        <TextField
                                                label="Payment Amount"
                                                type="number"
                                                value={amount}
                                                onChange={(e) => setAmount(e.target.value)}
                                                fullWidth
                                                sx={{ mb: 3 }}
                                                InputProps={{
                                                        startAdornment: <InputAdornment position="start">£</InputAdornment>,
                                                }}
                                                placeholder="0.00"
                                                helperText="Enter £0 to be prompted for an amount on Gov.uk"
                                                inputProps={{ min: 0, step: 0.01 }}
                                        />

                                        <Button
                                                variant="contained"
                                                size="large"
                                                fullWidth
                                                onClick={handlePayment}
                                                disabled={loading || !reference}
                                                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                                                sx={{ py: 1.5 }}
                                        >
                                                {loading ? 'Processing...' : 'Pay with Gov.uk'}
                                        </Button>
                                </CardContent>
                        </Card>

                        <Paper elevation={0} sx={{ mt: 3, p: 2, bgcolor: 'grey.100' }}>
                                <Typography variant="caption" color="text.secondary">
                                        You will be redirected to the secure Gov.uk Pay service to complete your payment.
                                        Your payment details are handled securely by HMRC.
                                </Typography>
                        </Paper>
                </Box>
        );
};

export default HMRCPayment;
